import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { Button } from '@/components/Button';
import { Compose } from '@/components/Icons/Compose';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { Snackbar } from '@/components/Snackbar';
import { ComposeModal } from '@/pages/Mail/components/ComposeModal';
import { MailAttachmentPreview } from '@/pages/Mail/components/MailAttachmentPreview';
import { MailDetail } from '@/pages/Mail/components/MailDetail';
import { MailFolderTabs } from '@/pages/Mail/components/MailFolderTabs';
import { MailList } from '@/pages/Mail/components/MailList';
import { MailMobileToolbar } from '@/pages/Mail/components/MailMobileToolbar';
import { MailToolbar } from '@/pages/Mail/components/MailToolbar';
import { useMailActions } from '@/pages/Mail/hooks/useMailActions';
import { useMailMessage } from '@/pages/Mail/hooks/useMailMessage';
import type { useMailMessages } from '@/pages/Mail/hooks/useMailMessages';
import type { useMailSearch } from '@/pages/Mail/hooks/useMailSearch';
import { useMailSelection } from '@/pages/Mail/hooks/useMailSelection';
import {
  getMailFolderActions,
  MAIL_FOLDER_CONFIG,
} from '@/pages/Mail/mailFolders';
import {
  ButtonVariant,
  MailComposeMode,
  MailFolderType,
  SnackbarVariant,
} from '@/types/components';
import type { MailAttachment, MailFolder, MailMessage } from '@/types/mail';

interface ComposeState {
  mode: MailComposeMode;
  sourceMail?: MailMessage;
}

interface Props {
  isMobile: boolean;
  folders: MailFolder[];
  folder: MailFolder;
  messages: ReturnType<typeof useMailMessages>;
  search: ReturnType<typeof useMailSearch>;
  onFolderChange: (folderType: MailFolderType) => void;
}

export const MailboxView: React.FC<Props> = ({
  isMobile,
  folders,
  folder,
  messages,
  search,
  onFolderChange,
}) => {
  const { t } = useTranslation();
  const { guardAction } = useProfileRestriction();
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletePermanentlyModalOpen, setIsDeletePermanentlyModalOpen] =
    useState(false);
  const [detailDeleteId, setDetailDeleteId] = useState<string | null>(null);
  const [isSentSnackbarOpen, setIsSentSnackbarOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] =
    useState<MailAttachment | null>(null);

  const { selectedMail, selectedMailId } = messages;
  const { mail: loadedMail, isLoading: isLoadingMail } =
    useMailMessage(selectedMailId);
  const detailMail = loadedMail
    ? messages.applyLocalChanges(loadedMail)
    : selectedMail;
  const config = MAIL_FOLDER_CONFIG[folder.type];
  const { canDelete, canDeletePermanently } = getMailFolderActions(folder);
  const detailDeleteTitle = canDeletePermanently
    ? t('mail.detail.deletePermanentlyConfirm.title')
    : t('mail.detail.deleteConfirm.title');
  const detailDeleteMessage = canDeletePermanently
    ? t('mail.detail.deletePermanentlyConfirm.message')
    : t('mail.detail.deleteConfirm.message');
  const selection = useMailSelection(messages.mails, folder.type);

  const {
    isPending,
    isMarkAllReadSnackbarOpen,
    isErrorSnackbarOpen,
    markAllRead,
    markSelectedRead,
    deleteSelected,
    restoreSelected,
    deleteSelectedPermanently,
    deleteMail,
    deleteMailPermanently,
    dismissMarkAllReadSnackbar,
    dismissErrorSnackbar,
  } = useMailActions({ folderType: folder.type, messages, selection });

  const deleteDetailMail = canDeletePermanently
    ? deleteMailPermanently
    : deleteMail;

  const openCompose = (state: ComposeState) =>
    guardAction(() => setCompose(state));

  const handleSent = () => {
    setCompose(null);
    setIsSentSnackbarOpen(true);
    messages.refresh();
  };

  const openMail = (id: string) => {
    if (config.opensCompose) {
      openCompose({
        mode: MailComposeMode.Draft,
        sourceMail: messages.findMail(id),
      });
      return;
    }

    messages.openMail(id);
  };

  const confirmDelete = () => {
    deleteSelected();
    setIsDeleteModalOpen(false);
  };

  const confirmDeletePermanently = () => {
    deleteSelectedPermanently();
    setIsDeletePermanentlyModalOpen(false);
  };

  const confirmDetailDelete = () => {
    if (!detailDeleteId) {
      return;
    }

    deleteDetailMail(detailDeleteId, messages.closeMail);
    setDetailDeleteId(null);
  };

  return (
    <>
      <div
        key={search.returnCount}
        className="flex min-h-0 flex-1 flex-col gap-4 max-md:animate-zoom-in md:p-6"
      >
        <div className="hidden md:block">
          <MailFolderTabs
            folders={folders}
            folder={folder}
            onChange={onFolderChange}
            onCompose={() => openCompose({ mode: MailComposeMode.New })}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card shadow-card max-md:rounded-t-2xl md:rounded-[20px]">
          {detailMail && (
            <MailDetail
              mail={detailMail}
              isMobile={isMobile}
              isLoading={isLoadingMail || isPending}
              showFollowUp={config.showFollowUp}
              showReplyForward={config.showReplyForward}
              canDelete={canDelete || canDeletePermanently}
              onBack={messages.closeMail}
              onCycleFollowUp={messages.cycleFollowUp}
              onDelete={setDetailDeleteId}
              onReply={(mail) =>
                openCompose({ mode: MailComposeMode.Reply, sourceMail: mail })
              }
              onForward={(mail) =>
                openCompose({ mode: MailComposeMode.Forward, sourceMail: mail })
              }
              onOpenAttachment={setPreviewAttachment}
            />
          )}
          {!detailMail && (
            <>
              {!isMobile && (
                <MailToolbar
                  folder={folder}
                  count={messages.totalCount}
                  searchQuery={search.searchQuery}
                  searchType={search.searchType}
                  isSelecting={selection.isSelecting}
                  selectedCount={selection.selectedIds.size}
                  areAllSelected={selection.areAllSelected}
                  onSearchChange={search.changeQuery}
                  onSearchTypeChange={search.changeSearchType}
                  onSearchSubmit={search.submitSearch}
                  onMark={selection.startSelecting}
                  onMarkAllRead={markAllRead}
                  onMarkSelectedRead={markSelectedRead}
                  onToggleAll={selection.toggleSelectAll}
                  onDelete={() => setIsDeleteModalOpen(true)}
                  onRestore={restoreSelected}
                  onDeletePermanently={() =>
                    setIsDeletePermanentlyModalOpen(true)
                  }
                  onCancel={selection.exitSelection}
                />
              )}
              {isMobile && (
                <MailMobileToolbar
                  folders={folders}
                  folder={folder}
                  count={messages.totalCount}
                  isSelecting={selection.isSelecting}
                  selectedCount={selection.selectedIds.size}
                  areAllSelected={selection.areAllSelected}
                  onFolderChange={onFolderChange}
                  onSearch={search.openSearch}
                  onMark={selection.startSelecting}
                  onMarkAllRead={markAllRead}
                  onMarkSelectedRead={markSelectedRead}
                  onToggleAll={selection.toggleSelectAll}
                  onDelete={() => setIsDeleteModalOpen(true)}
                  onRestore={restoreSelected}
                  onDeletePermanently={() =>
                    setIsDeletePermanentlyModalOpen(true)
                  }
                  onCancel={selection.exitSelection}
                />
              )}
              <MailList
                folderType={folder.type}
                mails={messages.mails}
                isLoading={messages.isLoading || isPending}
                isSelecting={selection.isSelecting}
                isMobile={isMobile}
                isSearching={search.isSearching}
                hasMorePages={messages.hasMorePages}
                isLoadingMore={messages.isFetchingNextPage}
                selectedIds={selection.selectedIds}
                scrollOffsetRef={messages.scrollOffsetRef}
                onCycleFollowUp={messages.cycleFollowUp}
                onToggleSelect={selection.toggleSelect}
                onOpen={openMail}
                onLoadMore={messages.loadMorePages}
              />
            </>
          )}
        </div>
      </div>

      {isMobile && !detailMail && (
        <div className="fixed right-4 bottom-[calc(88px+env(safe-area-inset-bottom))] z-30">
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => openCompose({ mode: MailComposeMode.New })}
            className="shadow-card"
          >
            <Compose className="size-5" />
            {t('mail.compose')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        title={t('mail.deleteConfirm.title')}
        message={t('mail.deleteConfirm.message')}
        confirmLabel={t('mail.delete')}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        isOpen={isDeletePermanentlyModalOpen}
        title={t('mail.deletePermanentlyConfirm.title')}
        message={t('mail.deletePermanentlyConfirm.message')}
        confirmLabel={t('mail.delete')}
        onClose={() => setIsDeletePermanentlyModalOpen(false)}
        onConfirm={confirmDeletePermanently}
      />

      <ConfirmDialog
        isOpen={!!detailDeleteId}
        title={detailDeleteTitle}
        message={detailDeleteMessage}
        confirmLabel={t('mail.delete')}
        onClose={() => setDetailDeleteId(null)}
        onConfirm={confirmDetailDelete}
      />

      <ComposeModal
        isOpen={!!compose}
        mode={compose?.mode ?? MailComposeMode.New}
        folderType={folder.type}
        sourceMail={compose?.sourceMail}
        onClose={() => setCompose(null)}
        onSent={handleSent}
      />

      {previewAttachment && (
        <MailAttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      <Snackbar
        isOpen={isMarkAllReadSnackbarOpen}
        message={t('mail.markAllAsReadSuccess')}
        onDismiss={dismissMarkAllReadSnackbar}
      />

      <Snackbar
        isOpen={isSentSnackbarOpen}
        message={t('mail.messageSentSuccess')}
        onDismiss={() => setIsSentSnackbarOpen(false)}
      />

      <BodyPortal>
        <Snackbar
          isOpen={search.hasMinLengthError}
          variant={SnackbarVariant.Error}
          message={t('mail.searchMinLength')}
          onDismiss={search.dismissMinLengthError}
        />
      </BodyPortal>

      <BodyPortal>
        <Snackbar
          isOpen={isErrorSnackbarOpen}
          variant={SnackbarVariant.Error}
          message={t('global.errorOccurred')}
          onDismiss={dismissErrorSnackbar}
        />
      </BodyPortal>
    </>
  );
};
